import { myAxios } from "../../utils/common";
import type { TestCase } from "./jsTestRunner";

export interface QuizQuestionDto {
  id: number;
  type: "TEST" | "CODE" | "REACT";
  difficulty: 1 | 2 | 3;
  topic: { code: string; title: string };
  /** description.answer sunucuda cikarilir — oturum sirasinda cozum gelmez */
  description: {
    title: string;
    preText?: string;
    options?: string[];
    lang?: string;
  };
  tests: { harness: string; entry: string; cases: TestCase[] } | null;
  /** Yalnizca egitmene (role 34) gelir — ogrenci yanitinda bu alanlar yok. */
  instructorAnswer?: string;
  instructorCases?: { args: unknown[]; expected: unknown }[];
  /** Yalnizca tek soru onizlemesinde (`/soru-bankasi/:id`) gelir. */
  moduleId?: number | null;
  active?: boolean;
}

export interface QuizSessionDto {
  sessionId: number;
  moduleId: number;
  total: number;
  answeredCount: number;
  completedAt: string | null;
  score: number | null;
  questions: QuizQuestionDto[];
  answered: number[];
}

export interface AnswerResultDto {
  questionId: number;
  isCorrect: boolean;
  score: number;
  feedback: string;
  gradedBy: "test" | "exact" | "ai";
  /** Kacinci deneme (1 = ilk). */
  attempt: number;
  /** Rapora/ustaliga yazilan puan — ILK denemeden, tekrar denemeyle degismez. */
  recordedScore: number;
  recordedCorrect: boolean;
  xpEarned?: number;
  totalXp?: number;
  currentStreak?: number;
  level?: number;
}

export interface TopicBreakdown {
  code: string;
  title: string;
  module_id: number;
  asked: number;
  correct: number;
}

export interface QuizSummaryDto {
  sessionId: number;
  moduleId: number;
  score: number;
  answered: number;
  total: number;
  correct: number;
  /** Ilk denemede dogru cevaplanan. */
  firstTryCorrect?: number;
  /** Ilk denemede ya da tekrar deneyerek cozulen. */
  solved?: number;
  topics: TopicBreakdown[];
  weakTopics: TopicBreakdown[];
}

export interface ModuleQuizStatus {
  moduleId: number;
  available: boolean;
  fullyAssigned: boolean;
  questionCount: number;
  /** Havuz buyuklugune gore 10-20 arasi; kucuk havuzda set de kucuk. */
  setSize: number;
  attempts: number;
  lastScore: number | null;
  lastAt: string | null;
  openSessionId: number | null;
  /** Ogrencinin cohort'una modulde atanan unit sayisi / bitirdigi. */
  unitsTotal?: number;
  unitsDone?: number;
  /** Son bitirilen set: ilk denemede dogru / toplam cozulen / soru sayisi. */
  lastFirstTryCorrect?: number | null;
  lastSolved?: number | null;
  lastTotal?: number | null;
}

export const quizApi = {
  start: (moduleId: number) =>
    myAxios.get<QuizSessionDto>(`/module-practice/${moduleId}/start`).then((r) => r.data),

  status: (moduleId: number) =>
    myAxios.get<ModuleQuizStatus>(`/module-practice/${moduleId}/status`).then((r) => r.data),

  /** Birden fazla modulun durumu tek istekte — "Tum Dersler" sayfasi icin. */
  statusBatch: (moduleIds: number[]) =>
    myAxios
      .get<ModuleQuizStatus[]>(`/module-practice/status`, {
        params: { moduleIds: moduleIds.join(",") },
      })
      .then((r) => r.data),

  session: (sessionId: number) =>
    myAxios.get<QuizSessionDto>(`/module-practice/session/${sessionId}`).then((r) => r.data),

  answer: (
    sessionId: number,
    payload: {
      questionId: number;
      answer: string;
      clientTestResult?: {
        passed: number;
        total: number;
        kind?: "structural" | "assertion";
      };
    },
  ) =>
    myAxios
      .post<AnswerResultDto>(`/module-practice/session/${sessionId}/answer`, payload)
      .then((r) => r.data),

  finish: (sessionId: number) =>
    myAxios
      .post<QuizSummaryDto>(`/module-practice/session/${sessionId}/finish`)
      .then((r) => r.data),

  mastery: () => myAxios.get(`/module-practice/mastery`).then((r) => r.data),

  /** Tek soru onizleme — yalnizca egitmen (role 34); ogrenciye 403 doner. */
  question: (questionId: number) =>
    myAxios.get<QuizQuestionDto>(`/module-practice/question/${questionId}`).then((r) => r.data),

  /** Onizlemede degerlendirme: oturuma/ustaliga/XP'ye yazmaz. */
  checkQuestion: (
    questionId: number,
    payload: {
      answer: string;
      clientTestResult?: { passed: number; total: number; kind?: "structural" | "assertion" };
    },
  ) =>
    myAxios
      .post<AnswerResultDto>(`/module-practice/question/${questionId}/check`, payload)
      .then((r) => r.data),
};
